const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const inrPaiseFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A formatter with `minimumFractionDigits: 0` prints ₹6,08,230.4 — a lone digit
 * after the point reads as a typo on a money figure. Whole amounts stay
 * compact; anything with a fraction is padded to paise.
 */
function padPaise(text: string): string {
  const dot = text.indexOf('.');
  return dot !== -1 && text.length === dot + 2 ? `${text}0` : text;
}

/** An amount in its own currency (₹ for INR or none, $ for USD, …). */
export function formatMoney(value?: number | null, currency?: string | null): string {
  const code = String(currency || 'INR').toUpperCase();
  if (code === 'INR') return formatINR(value);
  const amount = Number(value ?? 0);
  try {
    return padPaise(new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 2 })
      .format(Number.isFinite(amount) ? amount : 0));
  } catch {
    return `${code} ${amount.toLocaleString('en-IN')}`;
  }
}

/** The currency a sales document is billed in (INR unless converted). */
export function docCurrency(doc?: { currencyConversion?: boolean; fromCurrency?: string } | null): string {
  const code = String(doc?.fromCurrency || 'INR').toUpperCase();
  return doc?.currencyConversion && code !== 'INR' ? code : 'INR';
}

/** A sales document's grand total in its own currency — "$390" for a USD quotation. */
export function formatDocTotal(doc?: { grandTotal?: number; currencyConversion?: boolean; fromCurrency?: string } | null): string {
  return formatMoney(doc?.grandTotal || 0, docCurrency(doc));
}

/** The same total in ₹ (at the document's own rate), or '' for an INR document. */
export function docInrEquivalent(doc?: { grandTotal?: number; currencyConversion?: boolean; fromCurrency?: string; conversionRate?: number } | null): string {
  if (docCurrency(doc) === 'INR' || !(Number(doc?.conversionRate) > 0)) return '';
  return formatINR(Math.round((doc?.grandTotal || 0) * Number(doc?.conversionRate)));
}

/** Single display standard for the India-first finance workspace. */
export function formatINR(value?: number | null): string {
  const amount = Number(value ?? 0);
  return padPaise(inrFormatter.format(Number.isFinite(amount) ? amount : 0));
}

/** Indian grouping with paise always shown — unit rates, price lists, printed labels. */
export function formatINRPaise(value?: number | null): string {
  const amount = Number(value ?? 0);
  return inrPaiseFormatter.format(Number.isFinite(amount) ? amount : 0);
}
